import { ChevronDownIcon, Chip, cn, Sheet } from "@au-schein-de/au-ui-library";
import {
	type KeyboardEvent,
	type ReactNode,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
	PHARMACY_PICKER_FILTER_SHEET_CONTENT_CLASS,
	PHARMACY_PICKER_FILTER_SHEET_Z_CLASS,
	PharmacyPickerFilterSheetHeader,
} from "~/features/products/pharmacy-picker/pharmacy-picker-filter-sheet-header.component";

const PANEL_GAP_PX = 8;

/**
 * Shared shell for the picker's filter-row dropdowns (Sort, Radius, and now
 * delivery method): a gray-200 "Filter Dropdown selection" chip (Figma 602)
 * that opens a side panel docked to the picker's right edge on desktop, and
 * a bottom sheet on mobile.
 *
 * PharmacyPickerSortMenu and RadiusSelect predate this and still carry their
 * own copies of this same positioning logic — this is the version new
 * dropdowns should use; migrating those two is a follow-up, not bundled here
 * to avoid re-touching already-shipped, already-tested components.
 */
function panelStyleForAnchor(
	anchor: DOMRect,
	panelWidthPx: number,
): { top: number; left: number } {
	const fitsRight =
		anchor.right + PANEL_GAP_PX + panelWidthPx <= window.innerWidth;
	return {
		top: anchor.top,
		left: fitsRight
			? anchor.right + PANEL_GAP_PX
			: Math.max(PANEL_GAP_PX, anchor.left - PANEL_GAP_PX - panelWidthPx),
	};
}

function pickerContentForAnchor(anchor: HTMLDivElement | null): Element | null {
	return (
		anchor?.closest("[data-testid='pharmacy-picker-panel-slot']") ??
		anchor?.closest("[data-testid='pharmacy-picker-content']") ??
		null
	);
}

function activateOnEnterOrSpace(
	event: KeyboardEvent<HTMLElement>,
	action: () => void,
): void {
	if (event.key === "Enter" || event.key === " ") {
		event.preventDefault();
		action();
	}
}

export type FilterDropdownPanelRenderProps = {
	onClose: () => void;
	/** True when docked beside the picker instead of floating via `style`. */
	docked: boolean;
	style?: { top: number; left: number };
};

export function FilterDropdownChip({
	icon,
	label,
	chipClassName = "h-9 shrink-0 cursor-pointer gap-2 rounded-xl bg-gray-200 p-3 text-body-sm font-medium text-gray-700",
	testId,
	panelWidthPx,
	renderDesktopPanel,
	renderMobileSheetContent,
	sheetTitle,
	sheetTestId,
	/**
	 * Skips the desktop chip+panel entirely — for a dropdown that already has
	 * a desktop entry point elsewhere (delivery method: the header's own
	 * switcher), so the picker's filter row shouldn't duplicate it.
	 */
	hideDesktop = false,
}: {
	icon?: ReactNode;
	label: string;
	chipClassName?: string;
	/** Base test id; the mobile trigger gets `${testId}-mobile`. */
	testId: string;
	panelWidthPx: number;
	/** Required unless `hideDesktop` is set. */
	renderDesktopPanel?: (props: FilterDropdownPanelRenderProps) => ReactNode;
	renderMobileSheetContent: (props: { onClose: () => void }) => ReactNode;
	sheetTitle: string;
	sheetTestId: string;
	hideDesktop?: boolean;
}) {
	const { t } = useTranslation("common");
	const [desktopOpen, setDesktopOpen] = useState(false);
	const [sheetOpen, setSheetOpen] = useState(false);
	const [panelStyle, setPanelStyle] = useState<{ top: number; left: number }>();
	const [pickerContent, setPickerContent] = useState<Element | null>(null);
	const desktopAnchorRef = useRef<HTMLDivElement>(null);
	const dockedToPicker = pickerContent != null;

	const toggleDesktopOpen = () => {
		const next = !desktopOpen;
		setDesktopOpen(next);
		setPickerContent(
			next ? pickerContentForAnchor(desktopAnchorRef.current) : null,
		);
	};

	useLayoutEffect(() => {
		if (!desktopOpen) {
			setPickerContent(null);
			return;
		}
		const picker = pickerContentForAnchor(desktopAnchorRef.current);
		setPickerContent(picker);
		if (picker) return;
		const updatePosition = () => {
			const rect = desktopAnchorRef.current?.getBoundingClientRect();
			if (!rect) return;
			setPanelStyle(panelStyleForAnchor(rect, panelWidthPx));
		};
		updatePosition();
		window.addEventListener("resize", updatePosition);
		window.addEventListener("scroll", updatePosition, true);
		return () => {
			window.removeEventListener("resize", updatePosition);
			window.removeEventListener("scroll", updatePosition, true);
		};
	}, [desktopOpen, panelWidthPx]);

	return (
		<>
			{hideDesktop ? null : (
				<>
					<div
						className="relative shrink-0 max-md:hidden"
						ref={desktopAnchorRef}
					>
						<Chip
							variant="filter"
							className={cn(
								chipClassName,
								"border-2",
								desktopOpen ? "border-ansay-700" : "border-transparent",
							)}
							data-testid={testId}
							// biome-ignore lint/a11y/useSemanticElements: Chip is the visual control and is not a native button
							role="button"
							tabIndex={0}
							aria-expanded={desktopOpen}
							onPointerDown={(event) => event.stopPropagation()}
							onClick={toggleDesktopOpen}
							onKeyDown={(event) => {
								activateOnEnterOrSpace(event, toggleDesktopOpen);
							}}
						>
							{icon}
							{label}
							<span className={cn("flex", desktopOpen && "rotate-180")}>
								<ChevronDownIcon size={5} />
							</span>
						</Chip>
					</div>
					{desktopOpen && renderDesktopPanel
						? createPortal(
								renderDesktopPanel({
									onClose: () => setDesktopOpen(false),
									docked: dockedToPicker,
									style: dockedToPicker ? undefined : panelStyle,
								}),
								pickerContent ?? document.body,
							)
						: null}
				</>
			)}
			<div className="shrink-0 md:hidden">
				<Chip
					variant="filter"
					className={cn(
						chipClassName,
						"border-2",
						sheetOpen ? "border-ansay-700" : "border-transparent",
					)}
					data-testid={`${testId}-mobile`}
					// biome-ignore lint/a11y/useSemanticElements: Chip is the visual control and is not a native button
					role="button"
					tabIndex={0}
					onPointerDown={(event) => event.stopPropagation()}
					onClick={() => setSheetOpen(true)}
					onKeyDown={(event) => {
						activateOnEnterOrSpace(event, () => setSheetOpen(true));
					}}
				>
					{icon}
					{label}
					<span className={cn("flex", sheetOpen && "rotate-180")}>
						<ChevronDownIcon size={5} />
					</span>
				</Chip>
				<Sheet.Root open={sheetOpen} onOpenChange={setSheetOpen}>
					<Sheet.Portal>
						<Sheet.Overlay className={PHARMACY_PICKER_FILTER_SHEET_Z_CLASS} />
						<Sheet.Content
							side="bottom"
							className={PHARMACY_PICKER_FILTER_SHEET_CONTENT_CLASS}
							data-testid={sheetTestId}
							aria-describedby={undefined}
						>
							<PharmacyPickerFilterSheetHeader
								title={sheetTitle}
								onClose={() => setSheetOpen(false)}
								closeLabel={t("close")}
								closeTestId={`${sheetTestId}-close`}
							/>
							{renderMobileSheetContent({
								onClose: () => setSheetOpen(false),
							})}
						</Sheet.Content>
					</Sheet.Portal>
				</Sheet.Root>
			</div>
		</>
	);
}

/**
 * Shared "is this pointer-down event inside a portaled filter-dropdown
 * panel" check for the picker's `onPointerDownOutside` dismiss guard —
 * matches by the panel's own testid rather than DOM position, since the
 * panel is portaled outside the picker's subtree.
 */
export function isFilterDropdownPanelEvent(
	event: { target: EventTarget | null; detail?: { originalEvent?: Event } },
	panelTestId: string,
): boolean {
	const target = event.detail?.originalEvent?.target ?? event.target;
	return (
		target instanceof Element &&
		Boolean(target.closest(`[data-testid="${panelTestId}"]`))
	);
}
